import React, { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { apiClient } from "../../App";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";

const PASS_SCORE = 100; // hijau + tersimpan hafal
const WARN_SCORE = 70;  // kuning: hampir benar

const WORD_COLOR = {
  correct: "text-green-600",
  wrong: "text-red-600",
  missing: "text-gray-400 underline decoration-dotted",
};
const BTN_COLOR = { pass: "text-green-600", warn: "text-yellow-500", fail: "text-red-600" };

export default function AyahRecorder({ verseKey, memorized = false, onPassed }) {
  const [recording, setRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const passedRef = useRef(onPassed);
  const memorizedRef = useRef(memorized);
  passedRef.current = onPassed;
  memorizedRef.current = memorized;

  const level = !result ? null : result.score >= PASS_SCORE ? "pass" : result.score >= WARN_SCORE ? "warn" : "fail";

  const send = async (blob, filename) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("verse_key", verseKey);
      fd.append("audio", blob, filename);
      const res = await apiClient.post("/recitation/check", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      setOpen(true);
      if (res.data.score >= PASS_SCORE && !memorizedRef.current) passedRef.current?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memeriksa bacaan");
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    if (recording) {
      recRef.current?.stop();
      setRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        send(new Blob(chunksRef.current, { type: mime }), mime.includes("webm") ? "rec.webm" : "rec.mp4");
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch (e) {
      toast.error("Tidak bisa mengakses mikrofon");
    }
  };

  const title =
    level === "pass" ? (memorized ? "Muraja'ah lancar" : "Hafalan berhasil, tersimpan") :
    level === "warn" ? "Hampir benar, coba ulangi" : "Belum berhasil, coba lagi";

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className={`rounded-full h-7 w-7 md:h-8 md:w-8 ${recording ? "text-red-600 animate-pulse" : level ? BTN_COLOR[level] : "text-muted-foreground"}`}
        onClick={toggle}
        disabled={loading}
        title={recording ? "Selesai" : "Rekam bacaan"}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className={level ? BTN_COLOR[level] : ""}>
              {title} ({result?.score}%)
            </DialogTitle>
          </DialogHeader>
          {result && (
            <div className="space-y-2">
              <div dir="rtl" className="flex flex-wrap gap-x-3 gap-y-2 text-2xl leading-loose">
                {result.words.map((w, i) => (
                  <span key={i} className={WORD_COLOR[w.status]}>{w.text}</span>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Hijau benar, merah salah, abu-abu terlewat.
                {result.extra?.length > 0 && " Ada kata tambahan yang terdengar."}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setOpen(false)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
